import { z } from "zod";
import { completionSourceFingerprintSchema } from "./completionSourceFingerprint";
import { trackedJobIdSchema } from "./legacyWorkflowIdentity";

export const completionDeliveryIdSchema = z.string().min(1).max(256).refine(value => value.trim() === value);
const id = completionDeliveryIdSchema;
const fingerprint = z.string().regex(/^[a-f0-9]{64}$/);
export const completionUndoRecoveryGuardSchema = z.object({ requestReactId: id, previewId: id,
  registrationDigest: fingerprint, requestFingerprint: fingerprint, expectedCatalogRevision: z.number().int().nonnegative(),
}).strict();
export type CompletionUndoRecoveryGuard = z.infer<typeof completionUndoRecoveryGuardSchema>;
const fields = {
  expectedOwnerId: id, expectedCompanyId: id, jobId: trackedJobIdSchema, sourceFingerprint: completionSourceFingerprintSchema,
  workspaceSceneId: id.nullable(), effectIndex: z.number().int().min(0).max(999),
};
export const completionDeliveryRequestSchema = z.discriminatedUnion("mode", [
  z.object({ ...fields, mode: z.literal("inspect") }).strict(),
  z.object({ ...fields, mode: z.literal("apply"), expectedFingerprint: fingerprint }).strict(),
  z.object({ ...fields, mode: z.literal("undo"), expectedFingerprint: fingerprint, recovery: completionUndoRecoveryGuardSchema.optional() }).strict(),
]);
export type CompletionDeliveryRequest = z.infer<typeof completionDeliveryRequestSchema>;
const responseSchema = z.object({
  outcome: z.enum(["ready", "applied", "replayed", "undone", "undo_replayed"]), ownerId: id, companyId: id, jobId: trackedJobIdSchema,
  sourceFingerprint: completionSourceFingerprintSchema, workspaceSceneId: id.nullable(), effectId: z.string(),
  component: z.object({ id }).passthrough().nullable(), fingerprint: fingerprint.nullable(),
  receipt: z.object({ effectId: z.string(), componentIds: z.array(id).min(1).max(20), appliedAt: z.number().finite() }).strict().nullable(),
}).strict();
export type CompletionDeliveryResponse = z.infer<typeof responseSchema>;

/** Bind every reply to the original requested source and target, including replay after deletion. */
export function parseCompletionDeliveryResponse(input: CompletionDeliveryRequest, componentId: string, value: unknown): CompletionDeliveryResponse {
  id.parse(componentId);
  const response = responseSchema.parse(value);
  const effectId = `component:${input.effectIndex}`;
  if (response.ownerId !== input.expectedOwnerId || response.companyId !== input.expectedCompanyId
    || response.jobId !== input.jobId || response.sourceFingerprint !== input.sourceFingerprint
    || response.workspaceSceneId !== input.workspaceSceneId || response.effectId !== effectId
    || (response.component === null) !== (response.fingerprint === null)
    || response.component && response.component.id !== componentId
    || input.mode === "inspect" && response.outcome === "applied"
    || input.mode === "apply" && response.outcome === "ready"
    || (input.mode === "undo") !== (response.outcome === "undone" || response.outcome === "undo_replayed")) throw new Error("Task delivery reply changed");
  if (response.outcome === "ready") {
    if (response.receipt || !response.component) throw new Error("Task delivery inspection is invalid");
  } else if (!response.receipt || response.receipt.effectId !== effectId || response.receipt.componentIds.length !== 1
    || response.receipt.componentIds[0] !== componentId) throw new Error("Task delivery receipt is invalid");
  return response;
}
